import { useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui-custom/Logo";
import { Link, useLocation } from "react-router-dom";
import {
  ThemeToggle,
  FullscreenToggle,
} from "@/components/ui-custom/ThemeToggle";

interface NavbarProps {
  onMenuToggle?: () => void;
}

export function Navbar({ onMenuToggle }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (onMenuToggle) {
      onMenuToggle();
    }
  };

  const isActive = (path: string) => {
    return currentPath === path;
  };

  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-lg bg-background/80 border-b border-border">
      <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="mr-2 md:hidden"
              onClick={toggleMenu}
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
            <Logo />
          </div>

          <nav className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className={`transition-colors ${
                isActive("/")
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
            >
              Home
            </Link>
            <Link
              to="/features"
              className={`transition-colors ${
                isActive("/features")
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
            >
              Features
            </Link>
            <Link
              to="/pricing"
              className={`transition-colors ${
                isActive("/pricing")
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
            >
              Pricing
            </Link>
            <Link
              to="/about"
              className={`transition-colors ${
                isActive("/about")
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
            >
              About
            </Link>
            <Link
              to="/feedback"
              className={`transition-colors ${
                isActive("/feedback")
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
            >
              Feedback
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <FullscreenToggle />
            <ThemeToggle />
            <div className="hidden sm:flex gap-2">
              <Button variant="ghost" asChild>
                <Link to="/auth/login">Log in</Link>
              </Button>
              <Button asChild>
                <Link to="/auth/signup">Sign up</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden animate-slide-down">
          <div className="py-2 px-4 space-y-1 bg-background/90 backdrop-blur-lg border-b border-border">
            <Link
              to="/"
              className={`block py-2 transition-colors ${
                isActive("/")
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/features"
              className={`block py-2 transition-colors ${
                isActive("/features")
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Features
            </Link>
            <Link
              to="/pricing"
              className={`block py-2 transition-colors ${
                isActive("/pricing")
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Pricing
            </Link>
            <Link
              to="/about"
              className={`block py-2 transition-colors ${
                isActive("/about")
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              About
            </Link>
            <Link
              to="/feedback"
              className={`block py-2 transition-colors ${
                isActive("/feedback")
                  ? "text-primary font-medium"
                  : "text-foreground hover:text-primary"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Feedback
            </Link>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" asChild className="w-full">
                <Link to="/auth/login">Log in</Link>
              </Button>
              <Button asChild className="w-full">
                <Link to="/auth/signup">Sign up</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
